import { memo } from 'react';
import { Box, Typography, useTheme, IconButton, Tooltip } from '@mui/material';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import { useStoreConfig } from '../../../../contexts/StoreConfigContext';

export type GridDensity = 'compact' | 'standard' | 'comfortable';

interface ProductsToolbarProps {
    productCount: number;
    sortOrder: 'price_asc' | 'price_desc' | null;
    setSortOrder: (order: 'price_asc' | 'price_desc' | null) => void;
    gridDensity: GridDensity;
    setGridDensity: (density: GridDensity) => void;
}

const GridDensityIcon = ({ bars, active, onClick, label }: {
    bars: number;
    active: boolean;
    onClick: () => void;
    label: string;
}) => {
    const theme = useTheme();
    const { config } = useStoreConfig();
    const barWidth = bars <= 3 ? 3 : 2;
    const gap = bars <= 3 ? 3 : 2;
    const totalWidth = bars * barWidth + (bars - 1) * gap;

    return (
        <Tooltip title={label} placement='top' arrow>
            <IconButton
                onClick={onClick}
                sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    backgroundColor: active ? `${config.primaryColor}18` : 'transparent',
                    '&:hover': { backgroundColor: active ? `${config.primaryColor}22` : theme.palette.action.hover },
                    transition: 'all 0.2s',
                }}
            >
                <svg width={totalWidth} height={16} viewBox={`0 0 ${totalWidth} 16`}>
                    {Array.from({ length: bars }).map((_, i) => (
                        <rect
                            key={i}
                            x={i * (barWidth + gap)}
                            y={0}
                            width={barWidth}
                            height={16}
                            rx={1}
                            fill={active ? config.primaryColor : theme.palette.text.disabled}
                        />
                    ))}
                </svg>
            </IconButton>
        </Tooltip>
    );
};

export const ProductsToolbar = memo(({
    productCount,
    sortOrder,
    setSortOrder,
    gridDensity,
    setGridDensity,
}: ProductsToolbarProps) => {
    const theme = useTheme();
    const { config } = useStoreConfig();

    const densityOptions: { density: GridDensity; bars: number; label: string }[] = [
        { density: 'comfortable', bars: 3, label: 'Large cards' },
        { density: 'standard', bars: 4, label: 'Medium cards' },
        { density: 'compact', bars: 6, label: 'Small cards' },
    ];

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                mb: 2,
                mr: 12,
                flexWrap: 'wrap',
                gap: 1.5,
            }}
        >
            {/* Right side — grid density toggles */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                <Typography variant='body2' color='text.secondary'>Viewing Options: &nbsp;</Typography>
                {densityOptions.map(({ density, bars, label }) => (
                    <GridDensityIcon
                        key={density}
                        bars={bars}
                        active={gridDensity === density}
                        onClick={() => setGridDensity(density)}
                        label={label}
                    />
                ))}
            </Box>
        </Box>
    );
});
